// One session in outline, from its record, for a reader about to walk someone
// through it (lib/investigateNarrate.js): what the person said, what it worked
// on (the rows the workspace draws for it: the files, repositories, hosts and
// helpers it touched, the ones it changed first), the sub-agents it started
// and what came of each, where something failed, its long pauses, the
// stretches the review summed up, and how it ended. Each line
// names its turn as the case reader's tools number them (T1 is the first
// message read) and, where it is one message, that message's id in
// [brackets], so a reader can open it before writing about it.
import { buildTraceLanes, isConversationDomain, markKindOf } from "./traceLanes.js";
import { helpersOf } from "./sessionWorkbench.js";

const PROMPTS_MAX = 16;
const LANES_MAX = 14;
const FAILS_MAX = 8;
const PAUSES_MAX = 5;
const CHAPTERS_MAX = 20;
const HELPERS_MAX = 8;
// A stretch with no message for this long is a pause worth saying.
const PAUSE_MS = 20 * 60 * 1000;

const str = (v) => (v == null ? "" : String(v));
const clip = (v, n) => {
    const s = str(v).replace(/\s+/g, " ").trim();
    return s.length > n ? `${s.slice(0, n - 1)}...` : s;
};
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const textOf = (c) => (typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join(" ") : "");
// What a call was asked to do, in a line: its command, path, address or query.
function callLine(b) {
    const inp = b?.input && typeof b.input === "object" ? b.input : {};
    const what = inp.command ?? inp.cmd ?? inp.file_path ?? inp.path ?? inp.url ?? inp.query ?? inp.pattern ?? inp.description;
    return `${b?.name || "a tool"}${what != null ? ` ${clip(what, 120)}` : b?.input != null ? ` ${clip(JSON.stringify(b.input), 120)}` : ""}`;
}

function span(ms) {
    const min = Math.round(ms / 60000);
    if (min < 60) return plural(min, "minute");
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h} h${m ? ` ${m} min` : ""}`;
}

// `turns`: the record as read (gap turns mark slices read apart). `total`:
// how many messages the record holds. `chapters`: the review's reports, as
// [{ summary, messages: [message ids] }]. Returns { text, named }: `named`
// the turns it names by id.
export function sessionOutline(turns, { total = null, chapters = [] } = {}) {
    const list = Array.isArray(turns) ? turns : [];
    const read = list.filter((t) => t && t.role !== "gap");
    if (!read.length) return { text: "", named: [] };
    const named = [];
    const at = (i) => {
        const t = list[i];
        if (t?.messageId) named.push(t);
        return `T${i + 1}${t?.messageId ? ` [${t.messageId}]` : ""}`;
    };
    let model = null;
    try {
        model = buildTraceLanes(list);
    } catch {
        model = null;
    }
    const lanes = model?.lanes || [];
    const groupOf = new Map();
    for (const g of model?.groups || []) for (const l of g.lanes || []) groupOf.set(l.id, g.label);
    const items = (lane) => (lane.items || []).filter((it) => Number.isInteger(it.turnIndex) && list[it.turnIndex]?.role !== "gap");

    const out = [];
    const times = read.map((t) => t.messageAt).filter(Number.isFinite);
    const lasted = times.length > 1 ? Math.max(...times) - Math.min(...times) : null;
    const whole = Number.isFinite(total) && total > read.length ? ` of ${total}` : "";
    out.push(`The session in outline, from its record: ${plural(read.length, "message")} read${whole}${lasted != null ? `, over ${span(lasted)}` : ""}. T counts messages from the first read; [brackets] hold message ids.`);

    // What the person said, in order: the first and last of a long run.
    const said = lanes.filter((l) => l.domain === "prompt").flatMap(items).sort((a, b) => a.turnIndex - b.turnIndex);
    if (said.length) {
        const shown = said.length > PROMPTS_MAX ? [...said.slice(0, PROMPTS_MAX / 2), null, ...said.slice(-PROMPTS_MAX / 2)] : said;
        out.push("", `What the person said (${said.length}):`);
        for (const it of shown) {
            if (!it) { out.push(`- ... ${said.length - PROMPTS_MAX} more ...`); continue; }
            out.push(`- ${at(it.turnIndex)}: ${it.op?.kind === "notice" ? "(notice) " : ""}"${clip(it.op?.text, 200)}"`);
        }
    }

    // What it worked on: every other row, the ones it changed or that
    // failed first, then the busiest.
    const rows = lanes.filter((l) => !isConversationDomain(l.domain)).map((l) => {
        const its = items(l);
        const changed = its.filter((it) => markKindOf(it) === "write");
        const kinds = [...new Set(changed.map((it) => str(it.op?.kind)).filter(Boolean))];
        const turnsOf = its.map((it) => it.turnIndex);
        return {
            lane: l, n: its.length, changed: changed.length, kinds, failed: its.filter((it) => it.op?.ok === false).length,
            from: turnsOf.length ? Math.min(...turnsOf) : null, to: turnsOf.length ? Math.max(...turnsOf) : null,
        };
    }).filter((r) => r.n > 0)
        .sort((a, b) => (b.changed * 3 + b.failed * 2 + b.n) - (a.changed * 3 + a.failed * 2 + a.n) || a.from - b.from);
    if (rows.length) {
        out.push("", "What it worked on (the investigation's rows for it, the ones it changed first):");
        for (const r of rows.slice(0, LANES_MAX)) {
            const where = r.from === r.to ? `T${r.from + 1}` : `T${r.from + 1}-T${r.to + 1}`;
            const what = [
                `touched ${r.n}x`,
                r.changed ? `changed ${r.changed}x${r.kinds.length ? ` (${r.kinds.slice(0, 3).join(", ")})` : ""}` : null,
                r.failed ? `${r.failed} failed` : null,
            ].filter(Boolean).join(", ");
            out.push(`- ${groupOf.get(r.lane.id) || r.lane.domain} > ${clip(r.lane.path || r.lane.label || r.lane.id, 100)}: ${what}; ${where}`);
        }
        if (rows.length > LANES_MAX) out.push(`- ... ${rows.length - LANES_MAX} more rows`);
    }

    // The sub-agents it started, as the workspace hangs them under its card:
    // what each was asked, and whether it reported back.
    const helpers = model ? helpersOf(model).filter((h) => Number.isInteger(h.spawnTurn) && list[h.spawnTurn]?.role !== "gap") : [];
    if (helpers.length) {
        out.push("", `Sub-agents it started (${helpers.length}${helpers.length > HELPERS_MAX ? `; the first ${HELPERS_MAX}` : ""}):`);
        for (const h of helpers.slice(0, HELPERS_MAX)) {
            const asked = h.sealed ? ", its task sealed by the provider" : h.prompt ? `, asked: "${clip(h.prompt, 160)}"` : "";
            const came = !h.reported ? "no report read"
                : h.failed ? `it failed${Number.isInteger(h.reportTurn) ? ` at ${at(h.reportTurn)}` : ""}`
                    : `reported back${Number.isInteger(h.reportTurn) ? ` at ${at(h.reportTurn)}` : ""}`;
            out.push(`- ${at(h.spawnTurn)}: ${clip(h.label || "a sub-agent", 80)}${h.agentType ? ` (${clip(h.agentType, 40)})` : ""}${asked}; ${came}`);
        }
    }

    // Where a call failed, the first of them: what it was asked to do, at
    // the turn that asked, and the start of the error.
    const calls = new Map();
    list.forEach((t, i) => {
        if (t?.role === "gap") return;
        for (const b of t?.blocks || []) if (b?.type === "tool_use" && b.id) calls.set(b.id, { b, i });
    });
    const failed = [];
    list.forEach((t) => {
        if (t?.role === "gap") return;
        for (const b of t?.blocks || []) {
            if (b?.type !== "tool_result" || !b.isError) continue;
            const call = calls.get(b.tool_use_id);
            if (call) failed.push({ ...call, error: textOf(b.content) });
        }
    });
    failed.sort((a, b) => a.i - b.i);
    if (failed.length) {
        out.push("", `Where a call failed (${failed.length}${failed.length > FAILS_MAX ? `; the first ${FAILS_MAX}` : ""}):`);
        for (const f of failed.slice(0, FAILS_MAX)) out.push(`- ${at(f.i)} ${callLine(f.b)}${f.error.trim() ? ` -> "${clip(f.error, 140)}"` : ""}`);
    }

    // Long pauses, as the record's own times say.
    const pauses = [];
    let last = null;
    list.forEach((t, i) => {
        if (t?.role === "gap" || !Number.isFinite(t?.messageAt)) return;
        if (last && t.messageAt - last.ms >= PAUSE_MS) pauses.push({ after: last.i, ms: t.messageAt - last.ms });
        last = { i, ms: t.messageAt };
    });
    if (pauses.length) {
        out.push("", "Long pauses:");
        for (const p of pauses.sort((a, b) => b.ms - a.ms).slice(0, PAUSES_MAX).sort((a, b) => a.after - b.after)) out.push(`- ${span(p.ms)} after T${p.after + 1}`);
    }

    // What the review said each stretch did, placed by the messages it read.
    const index = new Map();
    list.forEach((t, i) => { if (t?.messageId) index.set(str(t.messageId).toLowerCase(), i); });
    const placed = (Array.isArray(chapters) ? chapters : []).map((c) => {
        const where = (c?.messages || []).map((id) => index.get(str(id).toLowerCase())).filter(Number.isInteger);
        return where.length && str(c?.summary).trim() ? { from: Math.min(...where), to: Math.max(...where), summary: c.summary } : null;
    }).filter(Boolean).sort((a, b) => a.from - b.from);
    if (placed.length) {
        out.push("", "What the review said each stretch did:");
        const kept = placed.length > CHAPTERS_MAX ? [...placed.slice(0, CHAPTERS_MAX / 2), null, ...placed.slice(-CHAPTERS_MAX / 2)] : placed;
        for (const c of kept) out.push(c ? `- T${c.from + 1}-T${c.to + 1}: ${clip(c.summary, 260)}` : `- ... ${placed.length - CHAPTERS_MAX} more stretches ...`);
    }

    // How it ended: its last words to the person.
    const replies = lanes.filter((l) => l.domain === "reply").flatMap(items).sort((a, b) => a.turnIndex - b.turnIndex);
    const end = replies[replies.length - 1];
    if (end) out.push("", "How it ended (its last reply):", `- ${at(end.turnIndex)}: "${clip(end.op?.text, 320)}"`);

    return { text: out.join("\n"), named };
}
